import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, Button, Chip, Grid2, Paper, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { setInputs } from "../../../features/Booking-Form/slices/booking.slice";
import { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import {
  BookingType,
  NotSerializedBookingInputs
} from "../../../features/Booking-Form/typings/booking-inputs";
import { bookingTypeFromVisitorRegistrations } from "../../../features/Booking-Form/functions/visitor-registration.functions";
import { filterExistingExternal, initSetTeam } from "../utils/booking.utils";
import { useRemoteFetchTeamBookingColleagues } from "../../../hooks/Remote/Booking/useRemoteFetchTeamBookingColleagues";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { MS } from "../../Dialog/form-dialog.component";
import InputsTeamExternal from "../../Dialog/partials/inputs-team-external.component";
import {
  TeamVisitorInvitationRegistrationsPanel,
  type TeamVisitorInvitationConfirmPayload
} from "../partials/team-visitor-invitation-registrations-panel.component";

/**
 * @description Component that displays possible team members according to the previously selected restriction object.
 * @param props.nextStep as a function which goes to the next step in the booking process.
 * @param props.prevStep as a as a function which goes to the previous step in the booking process.
 * @param props.title as a string which declares the page title.
 * @param props.inputs as a BI which includes all the main inputs from the booking form component.
 * @param props.setInputs as a function which sets the inputs in the booking form component.
 * @version 0.1.0
 */

type Props = {
  title: string;
};

const FormTeamSelection: React.FC<Props> = ({ title }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useDispatch();
  const {
    userInformation,
    userInformation: { visitorManagementEnabled, sub, firstName, surname, photoUrl, email }
  } = useSelector((state: RootState) => state.login);
  const inputs = useSelector((state: RootState) => state.booking.inputs);
  const { usersBookedFor } = inputs;

  const showTeamVisitorInvitation = visitorManagementEnabled === true;

  const [options, setOptions] = useState<TeamMember[]>([]);
  const [team, setTeam] = useState<TeamMember[]>(
    usersBookedFor && usersBookedFor?.length >= 1 ? usersBookedFor : []
  );
  const [externals, setExternals] = useState<TeamMember[]>([]); // Legacy externals + visitor guests for autocomplete
  const [currentUser, setCurrentUser] = useState(0);

  const [externalModal, setExternalModal] = useState<MS<TeamMember>>({
    isOpen: false,
    data: undefined
  });
  const [visitorInvitationExpanded, setVisitorInvitationExpanded] = useState(false);
  const [errorSelection, setErrorSelection] = useState(false);

  const { data: teamBookingColleagueData } = useRemoteFetchTeamBookingColleagues();
  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();

  const replaceVisitorGuestsOnTeam = useCallback((guests: TeamMember[]) => {
    setExternals(prev => [...prev.filter(m => !m.isVisitor), ...guests]);
    setTeam(prev => [...prev.filter(m => !m.isVisitor), ...guests]);
  }, []);

  const openExternalModal = useCallback(() => {
    setExternalModal({
      isOpen: true,
      data: {
        userId: sub,
        firstName: "",
        surname: "",
        email: "",
        isExternal: true,
        isVisitor: false,
        company: ""
      }
    });
  }, [sub]);

  const handleExternalSubmit = useCallback(
    (values: Partial<TeamMember>) => {
      const ext: TeamMember = {
        userId: sub,
        firstName: values.firstName ?? "",
        surname: values.surname ?? "",
        email: values.email ?? "",
        company: values.company ?? "",
        isExternal: true,
        isVisitor: false,
        photoUrl: null
      };
      setExternals(prev => [...prev, ext]);
      setTeam(prev => [...prev, ext]);
      setExternalModal({ isOpen: false, data: null });
    },
    [sub]
  );

  const closeVisitorInvitation = useCallback(() => {
    setVisitorInvitationExpanded(false);
  }, []);

  const handleVisitorInvitationConfirm = useCallback(
    ({ visitorRegistrations: regs, guests }: TeamVisitorInvitationConfirmPayload) => {
      replaceVisitorGuestsOnTeam(guests);

      const derivedBookingType = bookingTypeFromVisitorRegistrations({
        ...inputs,
        visitorRegistrations: regs
      } as NotSerializedBookingInputs);
      const bookingTypePatch: Partial<NotSerializedBookingInputs> = {};
      if (derivedBookingType != null && inputs.bookingType !== derivedBookingType) {
        bookingTypePatch.bookingType = derivedBookingType;
      } else if (
        derivedBookingType == null &&
        inputs.mode === BookingType.TEAM &&
        inputs.bookingType !== BookingType.WORKPLACE
      ) {
        bookingTypePatch.bookingType = BookingType.WORKPLACE;
      }

      dispatch(
        setInputs({
          visitorRegistrations: regs,
          ...bookingTypePatch
        })
      );
      setVisitorInvitationExpanded(false);
    },
    [dispatch, inputs, replaceVisitorGuestsOnTeam]
  );

  /* istanbul ignore next */
  const handleTeamChange = (event: React.ChangeEvent<any>, values: TeamMember[]) => {
    event.persist();
    const teamMember = values.map(user => ({
      userBookedById: sub,
      userId: user.userId,
      firstName: user.firstName,
      surname: user.surname,
      isExternal: user.isExternal || false,
      isVisitor: user.isVisitor || false,
      bookPlace: user.bookPlace || false,
      isUserBookedBy: user.userId === sub,
      photoUrl: user.photoUrl,
      email: user.email,
      company: user.company
    }));

    if (!teamMember.length) {
      setErrorSelection(true);
      return setTimeout(() => setErrorSelection(false), 3000);
    }

    if (!teamMember[currentUser]) setCurrentUser(0);
    setTeam(teamMember);
  };

  useEffect(() => {
    // initial set
    initSetTeam(inputs, setTeam, userInformation);

    if (!teamBookingColleagueData || !remoteUsers) return;

    // in case, deduct user from team booking colleagues
    const teamBookingColleagues = teamBookingColleagueData
      .filter(data => data.userId !== userInformation.sub)
      .map(col => ({
        ...col,
        // update the photoUrl exists in all users data
        photoUrl: col.photoUrl ?? remoteUsers.find(user => user.userId === col.userId)?.photoUrl
      }));

    // set team booking colleagues from connection and set them as options
    setOptions(teamBookingColleagues);

    // set externals if there are already created external members in the booking selection process
    const existingExternal = filterExistingExternal(inputs, userInformation, teamBookingColleagues);
    setExternals(existingExternal);
  }, [userInformation, teamBookingColleagueData, remoteUsers]);

  useEffect(() => {
    dispatch(setInputs({ usersBookedFor: team }));
  }, [dispatch, inputs?.usersBookedFor?.length, team]);

  const currentUserOption = {
    userBookedById: sub,
    firstName,
    surname,
    isExternal: false,
    isUserBookedBy: true,
    userId: sub,
    photoUrl,
    email
  };

  const autocompleteOptions = [...options, ...externals, currentUserOption];

  const initialVisitorRegistrations = inputs.visitorRegistrations?.length
    ? inputs.visitorRegistrations
    : undefined;

  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        width: "100%",
        boxShadow: "none",
        gap: 1,
        backgroundImage: "none",
        backgroundColor: "unset",
        margin: "2% 0"
      }}
      data-testid="form-team-select-paper"
    >
      <Grid2 container sx={{ mb: 0.5 }}>
        <Typography variant={"h5"}>{title}</Typography>
      </Grid2>

      <Autocomplete
        data-testid="team-selection"
        multiple
        disableCloseOnSelect
        id="tags-outlined8"
        options={autocompleteOptions}
        renderOption={(props, option) => {
          if (autocompleteOptions.length > 1) {
            return (
              <li {...props} key={uuidv4()}>
                <div data-testid="team-selection-option">
                  {option.firstName + " " + option.surname}
                </div>
              </li>
            );
          }
          // when no options are existed
          return (
            <div key={uuidv4()}>
              <Button
                style={{ width: "100%" }}
                onClick={e => {
                  e.stopPropagation();
                  history.push("/connections");
                }}
                color={"primary"}
              >
                {t("Add Colleague")}
              </Button>
            </div>
          );
        }}
        isOptionEqualToValue={(option, value) =>
          // double-check because the external members share userId with logged-in user id
          option.firstName === value.firstName && value.email === option.email
        }
        sx={{ width: "100%", mb: 1 }}
        value={team}
        onChange={handleTeamChange}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            placeholder={t("Select your team members")}
            error={errorSelection}
            helperText={errorSelection ? t("At least one is required to start booking") : ""}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...rest } = getTagProps({ index });
            return (
              <Chip
                {...getTagProps({ index })}
                {...rest}
                label={option.firstName + " " + option.surname}
                key={key}
              />
            );
          })
        }
        getOptionLabel={option => option.firstName + " " + option.surname}
      />

      <Button
        data-testid="add-external-btn"
        onClick={openExternalModal}
        sx={{ textTransform: "none" }}
      >
        {t("Add External")}
      </Button>

      {showTeamVisitorInvitation && (
        <>
          <Button
            data-testid="team-booking-visitor-invitation-btn"
            onClick={() => {
              if (visitorInvitationExpanded) {
                closeVisitorInvitation();
              } else {
                setVisitorInvitationExpanded(true);
              }
            }}
            startIcon={visitorInvitationExpanded ? <RemoveIcon /> : <AddIcon />}
            sx={{ textTransform: "none" }}
          >
            {t("_visitorBooking_teamVisitorInvitationButton")}
          </Button>

          <TeamVisitorInvitationRegistrationsPanel
            bookerSub={sub}
            initialRegistrations={initialVisitorRegistrations}
            onConfirm={handleVisitorInvitationConfirm}
            visible={visitorInvitationExpanded}
          />
        </>
      )}

      <InputsTeamExternal
        modalState={externalModal}
        onCancel={() => setExternalModal({ isOpen: false, data: null })}
        onSubmit={handleExternalSubmit}
      />
    </Paper>
  );
};

export default FormTeamSelection;
